import { useState } from "react";
import { toast } from "sonner";
import { Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { submitQuickLead } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SERVICES = [
  "Painting",
  "Flooring",
  "Tile",
  "Trim / Doors",
  "Windows",
  "Drywall Repair",
  "Full Interior Remodel",
  "Other",
];

export default function Hero() {
  const [form, setForm] = useState({ name: "", phone: "", service: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in name, phone, and service.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitQuickLead(form);
      toast.success(res.message || "Thanks! We'll call you back shortly.");
      setForm({ name: "", phone: "", service: "" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send request. Please call 716-513-4765.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end pb-16 pt-28 overflow-hidden grain"
    >
      <div
        className="absolute inset-0 -z-10 bg-cover bg-center animate-subtle-zoom"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1638972691611-69633a3d3127?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc4MTE5NzUzfDA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/65 via-black/55 to-black/85" />

      <div className="container-x w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
        <div className="lg:col-span-7 text-black animate-fade-up">
          <p className="eyebrow text-black">
            Interior Construction · Western New York
          </p>
          <h1
            data-testid="hero-headline"
            className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight"
          >
            Clean interior remodeling
            <span className="block text-brand-rust">done right the first time.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-black leading-relaxed font-medium">
            Painting, flooring, trim, doors, tile, windows, drywall repair and
            finish work for homeowners across Niagara Falls, Buffalo, Lockport
            and surrounding WNY areas.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid="hero-estimate-button"
              className="btn-accent"
            >
              Book a Free Estimate <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="tel:17165134765"
              data-testid="hero-call-button"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-white hover:text-brand-ink transition-all"
            >
              <Phone className="h-4 w-4" /> 716-513-4765
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold text-black">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-rust" /> Free estimates
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-rust" /> Interior specialist
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-rust" /> 1-year workmanship warranty
            </span>
          </div>
        </div>

        <div
          className="lg:col-span-5 bg-white border border-brand-line p-7 md:p-8 animate-fade-up"
          data-testid="quick-form-card"
          style={{ animationDelay: "150ms" }}
        >
          <p className="eyebrow">Quick request</p>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-brand-ink">
            Request a call back
          </h2>
          <p className="mt-1 text-sm text-brand-sub">
            Send your project details and we'll follow up.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-5 space-y-3">
            <input
              data-testid="quick-name-input"
              type="text"
              className="field"
              placeholder="Your name"
              value={form.name}
              onChange={update("name")}
              required
            />
            <input
              data-testid="quick-phone-input"
              type="tel"
              className="field"
              placeholder="Phone number"
              value={form.phone}
              onChange={update("phone")}
              required
            />
            <Select value={form.service} onValueChange={update("service")}>
              <SelectTrigger
                data-testid="quick-service-trigger"
                className="field h-auto py-3"
              >
                <SelectValue placeholder="What do you need?" />
              </SelectTrigger>
              <SelectContent data-testid="quick-service-content">
                {SERVICES.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    data-testid={`quick-service-option-${s}`}
                  >
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              data-testid="quick-submit-button"
              disabled={loading}
              type="submit"
              className="btn-accent w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send Request"}
            </button>
            <p className="text-[11px] text-brand-sub text-center pt-1">
              No spam. We reply within one business day.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
